import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MarketStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => apiGet("/market/status").then(setStatus).catch(() => {});
  const loadHolidays = (y) => {
    setLoadingHolidays(true);
    apiGet(`/market/holidays/${y}`)
      .then(setHolidays)
      .catch(() => setHolidays([]))
      .finally(() => setLoadingHolidays(false));
  };

  useEffect(() => {
    loadStatus();
    loadHolidays(year);
    const id = setInterval(loadStatus, 60_000);
    return () => clearInterval(id);
  }, [year]);

  const addHoliday = async () => {
    if (!newDate || !newName.trim()) {
      toast.error("Date and name are required");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/market/holidays", { date: newDate, name: newName.trim() });
      toast.success(`Added "${newName}"`);
      setNewDate("");
      setNewName("");
      loadHolidays(year);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add holiday");
    } finally { setBusy(false); }
  };

  const removeHoliday = async (id, name) => {
    setBusy(true);
    try {
      await apiDelete(`/market/holidays/${id}`);
      toast.success(`Removed "${name}"`);
      loadHolidays(year);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove holiday");
    } finally { setBusy(false); }
  };

  const seedDefaults = async () => {
    setBusy(true);
    try {
      const res = await apiPost("/market/holidays/seed-defaults", {});
      if (res.already_seeded) {
        toast.info("Default holiday list is already seeded.");
      } else {
        toast.success(`Seeded ${res.inserted} default public holidays.`);
      }
      loadHolidays(year);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed defaults");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const isOpen = !!status.is_open;
  const dotCls = isOpen
    ? "bg-[hsl(var(--success))]"
    : "bg-[hsl(var(--destructive))]";
  const headlineCls = isOpen
    ? "text-[hsl(var(--success))]"
    : "text-[hsl(var(--destructive))]";

  let headline = "";
  if (isOpen) headline = `OPEN — closes in ${status.countdown_human}`;
  else if (status.state === "closed_weekend") headline = `CLOSED — Weekend (opens in ${status.countdown_human})`;
  else if (status.state === "closed_holiday") headline = "CLOSED — Public Holiday";
  else if (status.state === "closed_after_hours") headline = `CLOSED — After hours (opens in ${status.countdown_human})`;
  else if (status.state === "closed_before_open") headline = `CLOSED — Opens in ${status.countdown_human}`;
  else headline = "CLOSED";

  const years = [year - 1, year, year + 1];

  return (
    <Card className="border bg-card" data-testid="market-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-primary" />
          CSE Market Status
        </CardTitle>
        <CardDescription>
          Live trading-session status (Mon–Fri 09:30–14:30 Asia/Colombo) plus public-holiday calendar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border bg-muted/30 px-4 py-3 flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center justify-center h-2.5 w-2.5 rounded-full ${dotCls}`} />
          <span className={`text-sm font-semibold ${headlineCls}`} data-testid="market-status-headline">
            {headline}
          </span>
          <span className="text-xs text-muted-foreground ml-auto">{status.reason}</span>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Public holidays — non-trading days
            </Label>
            <div className="flex gap-1">
              {years.map((y) => (
                <Button
                  key={y}
                  size="sm"
                  variant={y === year ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={() => setYear(y)}
                  data-testid={`holidays-year-${y}`}
                >
                  {y}
                </Button>
              ))}
            </div>
          </div>

          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="holidays-table">
                {loadingHolidays ? (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
                ) : holidays.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center">
                      <AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-amber-500" />
                      <span className="text-muted-foreground">No holidays configured for {year}</span>
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={seedDefaults}
                          disabled={busy}
                          data-testid="holidays-seed-defaults"
                          className="gap-1.5"
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          Seed Sri Lanka 2026 defaults
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  holidays.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{h.date}</td>
                      <td className="px-3 py-2">
                        {h.name}
                        {h.date === status.now_colombo?.slice(0, 10) && (
                          <Badge variant="outline" className="ml-2 text-[10px]">Today</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                          onClick={() => removeHoliday(h.id, h.name)}
                          disabled={busy}
                          data-testid={`holiday-delete-${h.date}`}
                          aria-label={`Remove ${h.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Date</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9" data-testid="holiday-add-date" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Name</Label>
              <div className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Esala Full Moon Poya"
                  className="h-9"
                  data-testid="holiday-add-name"
                />
                <Button onClick={addHoliday} disabled={busy} className="gap-2 whitespace-nowrap" data-testid="holiday-add-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Add holiday
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketStatusCard;
